import React, { useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { FriendshipTier } from '../types/friends';

interface ConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (tier: FriendshipTier, note?: string) => Promise<void>;
  recipientName: string;
}

const TIERS: { id: FriendshipTier; label: string; shortLabel: string; color: string }[] = [
  {
    id: 'BASIC',
    label: 'Basic',
    shortLabel: 'Basic',
    color: 'text-gray-300'
  },
  {
    id: 'TIER_3',
    label: 'Bronze (Tier 3)',
    shortLabel: 'Bronze',
    color: '' // Custom color #A75217 applied via inline style
  },
  {
    id: 'TIER_2',
    label: 'Silver (Tier 2)',
    shortLabel: 'Silver',
    color: 'text-gray-400'
  },
  {
    id: 'TIER_1',
    label: 'Gold (Tier 1)',
    shortLabel: 'Gold',
    color: 'text-yellow-500'
  }
];

// Helper function to get tier image paths
const getTierImagePath = (tier: FriendshipTier): string => {
  const tierName = tier === 'TIER_1' ? 'gold' : tier === 'TIER_2' ? 'silver' : tier === 'TIER_3' ? 'bronze' : 'basic';
  return `/friend-tier-${tierName}.png`;
};

// Helper function to get tier descriptions
const getTierDescriptions = (tier: FriendshipTier): string[] => {
  switch (tier) {
    case 'TIER_1':
      return [
        '✅ All level %s and charts visible',
        '⛔ Portfolio $ values always hidden'
      ];
    case 'TIER_2':
      return [
        '✅ Portfolio and account level %s and charts visible',
        '⚠️ Only portfolio/account data displays here',
        '⛔ Individual ticker data hidden. $ values always hidden'
      ];
    case 'TIER_3':
      return [
        '✅ Portfolio level %s charts visible',
        '⚠️ Only portfolio data displays here',
        '⛔ Individual account and ticker data hidden. $ values always hidden'
      ];
    case 'BASIC':
      return [
        '💬 Only chat is enabled',
        '⛔ No portfolio data is visible'
      ];
  }
};

export const ConnectModal: React.FC<ConnectModalProps> = ({ isOpen, onClose, onConnect, recipientName }) => {
  const [selectedTier, setSelectedTier] = useState<FriendshipTier>('BASIC');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onConnect(selectedTier, note);
      onClose();
      setNote('');
      setSelectedTier('BASIC');
    } catch (error) {
      console.error('Failed to connect:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tierDescriptions = getTierDescriptions(selectedTier);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Connect with ${recipientName}`} size="md">
      <div className="space-y-4">
        {/* Choose Friendship Tier Label */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Choose Friendship Tier:</label>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4" aria-label="Tabs">
            {TIERS.map((tier) => (
              <button
                key={tier.id}
                onClick={() => setSelectedTier(tier.id)}
                className={`
                  whitespace-nowrap py-2 px-2 border-b-2 font-medium text-sm transition-colors flex items-center
                  ${selectedTier === tier.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                <span 
                  className={`mr-1 ${tier.color || ''}`}
                  style={tier.id === 'TIER_3' ? { color: '#A75217' } : undefined}
                >
                  ★
                </span>
                {tier.shortLabel}
              </button>
            ))}
          </nav>
        </div>

        {/* Tier Descriptions */}
        <div className="space-y-1 text-sm">
          {tierDescriptions.map((description, index) => (
            <p key={index} className="text-gray-700">{description}</p>
          ))}
        </div>

        {/* Visual Preview */}
        <div className="flex justify-center">
          <img
            src={getTierImagePath(selectedTier)}
            alt={`${TIERS.find(t => t.id === selectedTier)?.label} tier preview`}
            className="max-w-full h-auto rounded-lg"
          />
        </div>

        {/* Note Input */}
        <div>
          <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
            Add a note (optional)
          </label>
          <textarea
            id="note"
            rows={3}
            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending...' : 'Send Request'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
